import { useEffect, type ReactElement } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { moduleOrder } from "routes/routePermissions"
import { useAppSelector } from "hooks/hooksStore"
import useCommon from "hooks/useCommon"
import { RootState } from "store/store"
import { getAvailableModulePath, hasModuleRole, parseJwt } from "utils/helpers"
import { destroyAccessToken, getAccessToken, removeRefreshToken } from "utils/token"

interface Props {
  children: ReactElement
  moduleId: number
}

const ProtectedRoute = ({ children, moduleId }: Props): ReactElement | null => {
  const location = useLocation()
  const { authUser } = useAppSelector((state: RootState) => state.common)
  const { setAuthUser } = useCommon()
  const token = getAccessToken()
  const parsedUser = token ? parseJwt(token) : null

  useEffect(() => {
    if (authUser || !token) return

    if (parsedUser) {
      setAuthUser(parsedUser)
      return
    }

    destroyAccessToken()
    removeRefreshToken()
  }, [authUser, token, parsedUser])

  if (!token) {
    if (!sessionStorage.getItem("redirectUrl")) {
      sessionStorage.setItem("redirectUrl", location.pathname + location.search)
    }
    return <Navigate to="/" replace />
  }

  if (!authUser && token) return parsedUser ? null : <Navigate to="/" replace />

  const roles = authUser?.roles || []
  const hasAccess = hasModuleRole(roles, moduleId)

  if (hasAccess) return children

  const fallback = getAvailableModulePath(roles, moduleOrder)
  if (!fallback) return <Navigate to="/no-access" replace />

  return <Navigate to={fallback} replace />
}

export default ProtectedRoute
